import React, { useState, useMemo, useRef, useEffect } from "react";
import revaLogo from "../assets/reva-logo.jpg";
import "../pages/common.css";

/* ── Indian Currency Number to Words ── */
export function numberToWordsIndian(num) {
  if (num === null || num === undefined || isNaN(num)) return "Zero Rupees Only";
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function convertGroup(n) {
    if (n === 0) return "";
    if (n < 20) return a[n] + " ";
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + a[n % 10] : "") + " ";
    return a[Math.floor(n / 100)] + " Hundred " + (n % 100 !== 0 ? convertGroup(n % 100) : "");
  }

  const positive = Math.abs(num);
  const integerPart = Math.floor(positive);
  const decimalPart = Math.round((positive - integerPart) * 100);

  if (integerPart === 0 && decimalPart === 0) return "Zero Rupees Only";

  let crore = Math.floor(integerPart / 10000000);
  let remainder = integerPart % 10000000;
  let lakh = Math.floor(remainder / 100000);
  remainder = remainder % 100000;
  let thousand = Math.floor(remainder / 1000);
  remainder = remainder % 1000;
  let hundreds = remainder;

  let str = "";
  if (crore > 0) str += convertGroup(crore) + "Crore ";
  if (lakh > 0) str += convertGroup(lakh) + "Lakh ";
  if (thousand > 0) str += convertGroup(thousand) + "Thousand ";
  if (hundreds > 0) str += convertGroup(hundreds);

  str = str.trim();
  if (!str) str = "Zero";
  str += " Rupees";
  if (decimalPart > 0) {
    str += " and " + convertGroup(decimalPart).trim() + " Paise";
  }
  return str + " Only";
}

export default function PrintInvoiceModal({
  invoice,
  lines = [],
  products = [],
  categories = [],
  inventoryItems = [],
  onClose,
}) {
  const [format, setFormat] = useState("gst"); // "gst" | "luxury"
  const [zoomMode, setZoomMode] = useState("fit"); // "fit" | "full"
  const [sheetScale, setSheetScale] = useState(1);
  const [sheetHeight, setSheetHeight] = useState(1123);
  const sheetRef = useRef(null);

  // Load saved store info if any, otherwise default only official brand elements with empty fields
  const [storeInfo, setStoreInfo] = useState(() => {
    try {
      const saved = localStorage.getItem("reva_store_info");
      if (saved) return JSON.parse(saved);
    } catch (_) { }
    return {
      name: "REVA JEWELLERS",
      slogan: "THE TOUCH OF EVA",
      tagline: "सौन्दर्यम् | संस्कारः | समृद्धिः",
      address: "Reva Jewellers Atelier, Station Road, Anand, Gujarat - 388001",
      phone: "+91 98250 12345",
      email: "info@revajewellers.com",
      gstin: "24AAAAA0000A1Z5",
      state: "Gujarat (24)",
    };
  });
  const [showSettings, setShowSettings] = useState(false);

  const updateStoreField = (key, val) => {
    setStoreInfo((prev) => {
      const next = { ...prev, [key]: val };
      try {
        localStorage.setItem("reva_store_info", JSON.stringify(next));
      } catch (_) { }
      return next;
    });
  };

  /* Quick lookup maps */
  const productMap = useMemo(
    () => new Map(products.map((p) => [Number(p.productID), p])),
    [products]
  );
  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [Number(c.categoryID), c])),
    [categories]
  );
  const inventoryItemMap = useMemo(
    () => new Map(inventoryItems.map((i) => [Number(i.inventoryItemID), i])),
    [inventoryItems]
  );

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "—";

  const fmtAmt = (v) =>
    `₹${parseFloat(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtWt = (v) => (parseFloat(v) || 0).toFixed(3);

  /* Compute aggregate item statistics */
  const enrichedLines = useMemo(() => {
    return lines.map((l, idx) => {
      const prod = productMap.get(Number(l.productID)) || null;
      const cat = prod?.categoryID ? categoryMap.get(Number(prod.categoryID)) : null;
      const invItem = l.inventoryItemID
        ? inventoryItemMap.get(Number(l.inventoryItemID))
        : null;

      const grossWt = invItem?.grossWeight
        ? parseFloat(invItem.grossWeight)
        : l.billedWeight
          ? parseFloat(l.billedWeight)
          : 0;
      const lessWt = invItem?.lessWeight ? parseFloat(invItem.lessWeight) : 0;
      const netWt = invItem?.netWeight
        ? parseFloat(invItem.netWeight)
        : l.billedWeight
          ? parseFloat(l.billedWeight)
          : 0;

      const hsn = cat?.codeHSN || prod?.hsnCode || (prod?.purityStandard?.includes("925") ? "7113" : "7113");
      const purity = prod?.purityStandard || (invItem?.purityStandard ?? "—");
      const lineAmt = parseFloat(l.lineAmount || (l.quantity * l.unitPrice) || 0);
      const taxRate = parseFloat(l.taxPercentage || 0);
      const taxAmt = parseFloat(l.taxAmount || (lineAmt * taxRate / 100) || 0);
      const total = parseFloat(l.totalPrice || (lineAmt + taxAmt) || 0);

      return {
        sr: idx + 1,
        ...l,
        productName: prod?.productName || `Item #${l.productID}`,
        productCode: prod?.productCode || "—",
        hsn,
        purity,
        grossWt,
        lessWt,
        netWt,
        lineAmt,
        taxRate,
        taxAmt,
        total,
      };
    });
  }, [lines, productMap, categoryMap, inventoryItemMap]);

  /* Total weights & HSN group aggregation */
  const totals = useMemo(() => {
    let totGross = 0;
    let totLess = 0;
    let totNet = 0;
    let totTaxable = 0;
    let totTax = 0;
    let totGrossAmt = 0;

    const hsnMap = {};

    enrichedLines.forEach((item) => {
      totGross += item.grossWt;
      totLess += item.lessWt;
      totNet += item.netWt;
      totTaxable += item.lineAmt;
      totTax += item.taxAmt;
      totGrossAmt += item.total;

      const h = item.hsn || "—";
      if (!hsnMap[h]) {
        hsnMap[h] = { hsn: h, taxable: 0, taxRate: item.taxRate, taxAmt: 0 };
      }
      hsnMap[h].taxable += item.lineAmt;
      hsnMap[h].taxAmt += item.taxAmt;
    });

    return {
      totGross,
      totLess,
      totNet,
      totTaxable: invoice.totalAmount ? parseFloat(invoice.totalAmount) : totTaxable,
      totTax: invoice.totalTaxAmount ? parseFloat(invoice.totalTaxAmount) : totTax,
      totGrossAmt: invoice.grossAmount ? parseFloat(invoice.grossAmount) : totGrossAmt,
      hsnSummary: Object.values(hsnMap),
    };
  }, [enrichedLines, invoice]);

  /* Scale calculation for Fit-to-Screen on mobile and tablet */
  useEffect(() => {
    const calcScale = () => {
      if (typeof window === "undefined") return;
      const modalPadding = window.innerWidth <= 768 ? 20 : 32;
      const availWidth = Math.max(280, window.innerWidth - modalPadding);
      const baseWidth = 794; // 210mm standard A4 rendered width
      if (zoomMode === "fit" && availWidth < baseWidth) {
        const s = Math.min(1, Math.max(0.3, availWidth / baseWidth));
        setSheetScale(s);
      } else {
        setSheetScale(1);
      }

      if (sheetRef.current) {
        setSheetHeight(sheetRef.current.offsetHeight || 1123);
      }
    };

    calcScale();
    window.addEventListener("resize", calcScale);
    const t = setTimeout(calcScale, 150);
    return () => {
      window.removeEventListener("resize", calcScale);
      clearTimeout(t);
    };
  }, [zoomMode, format, enrichedLines]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="rv-print-modal-backdrop" onClick={onClose}>
      <div className="rv-print-modal-window" onClick={(e) => e.stopPropagation()}>

        {/* ── Top Floating Toolbar (Screen only) ── */}
        <div className="rv-print-toolbar">
          {/* Header Row: Title & Quick Icons */}
          <div className="rv-print-toolbar-header-row">
            <div className="rv-print-toolbar-title">
              <span className="rv-print-toolbar-icon"><PrinterIcon size={16} /></span>
              <span className="rv-print-toolbar-invnum">Invoice {invoice.invoiceNumber}</span>
              <span className="rv-print-toolbar-badge">{invoice.invoiceStatus || "Finalised"}</span>
            </div>

            <div className="rv-print-toolbar-top-actions">
              <button
                type="button"
                className={`rv-print-icon-btn ${showSettings ? "rv-print-icon-btn--active" : ""}`}
                onClick={() => setShowSettings((v) => !v)}
                title="Edit Store Header Details"
                aria-label="Settings"
              >
                <SettingsIcon size={15} />
              </button>
              <button
                type="button"
                className="rv-print-icon-btn rv-print-icon-btn--close"
                onClick={onClose}
                title="Close Preview"
                aria-label="Close"
              >
                <CloseIcon size={14} />
              </button>
            </div>
          </div>

          {/* Controls Row: Format and Zoom Toggle */}
          <div className="rv-print-toolbar-controls-row">
            {/* Format toggle: GST vs Slip */}
            <div className="rv-print-format-toggle">
              <button
                type="button"
                className={`rv-print-format-btn ${format === "gst" ? "rv-print-format-btn--active" : ""}`}
                onClick={() => setFormat("gst")}
              >
                Standard GST
              </button>
              <button
                type="button"
                className={`rv-print-format-btn ${format === "luxury" ? "rv-print-format-btn--active" : ""}`}
                onClick={() => setFormat("luxury")}
              >
                Luxury Slip
              </button>
            </div>

            {/* Scale toggle: Fit to Screen vs 100% Zoom */}
            <div className="rv-print-zoom-toggle">
              <button
                type="button"
                className={`rv-print-format-btn ${zoomMode === "fit" ? "rv-print-format-btn--active" : ""}`}
                onClick={() => setZoomMode("fit")}
                title="Scale to fit screen width"
              >
                <FitScreenIcon size={12} /> Fit
              </button>
              <button
                type="button"
                className={`rv-print-format-btn ${zoomMode === "full" ? "rv-print-format-btn--active" : ""}`}
                onClick={() => setZoomMode("full")}
                title="View actual 100% scale (pan to read)"
              >
                <ZoomIcon size={12} /> 100%
              </button>
            </div>
          </div>

          {/* Primary Action Button */}
          <div className="rv-print-toolbar-cta-row">
            <button
              type="button"
              className="rv-print-btn rv-print-btn--primary rv-print-btn--full"
              onClick={handlePrint}
            >
              <PrinterIcon size={16} /> Print / Save A4 PDF
            </button>
          </div>
        </div>

        {/* ── Optional Quick Store Info Editor ── */}
        {showSettings && (
          <div className="rv-print-settings-grid">
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 3 }}>Store Address</label>
              <input
                className="rv-input"
                style={{ fontSize: 11, padding: "5px 8px" }}
                placeholder="Enter store address"
                value={storeInfo.address}
                onChange={(e) => updateStoreField("address", e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 3 }}>GSTIN</label>
              <input
                className="rv-input"
                style={{ fontSize: 11, padding: "5px 8px" }}
                placeholder="Enter store GSTIN"
                value={storeInfo.gstin}
                onChange={(e) => updateStoreField("gstin", e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 3 }}>Contact Phone</label>
              <input
                className="rv-input"
                style={{ fontSize: 11, padding: "5px 8px" }}
                placeholder="Enter phone number"
                value={storeInfo.phone}
                onChange={(e) => updateStoreField("phone", e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 3 }}>State / Place of Supply</label>
              <input
                className="rv-input"
                style={{ fontSize: 11, padding: "5px 8px" }}
                placeholder="e.g. Gujarat (24)"
                value={storeInfo.state}
                onChange={(e) => updateStoreField("state", e.target.value)}
              />
            </div>
          </div>
        )}

        {/* ── Mobile Scroll Hint (Visible only when in 100% zoom mode) ── */}
        {zoomMode === "full" && (
          <div className="rv-a4-scroll-hint">
            <MoveHorizontalIcon size={13} />
            <span>Swipe sideways to inspect full size A4 invoice</span>
          </div>
        )}

        {/* ── Physical A4 Sheet Container ── */}
        <div
          className={`rv-a4-sheet-wrap ${zoomMode === "fit" ? "rv-a4-sheet-wrap--fit" : "rv-a4-sheet-wrap--full"}`}
          style={
            zoomMode === "fit" && sheetScale < 1
              ? {
                height: Math.ceil(sheetHeight * sheetScale) + 24,
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
              }
              : undefined
          }
        >
          <div
            ref={sheetRef}
            className="rv-a4-sheet"
            style={
              zoomMode === "fit" && sheetScale < 1
                ? {
                  transform: `scale(${sheetScale})`,
                  transformOrigin: "top center",
                  margin: "0 auto",
                  flexShrink: 0,
                }
                : undefined
            }
          >

            {/* ══════════════════════════════════════════════════
               FORMAT 1: STANDARD GST TAX INVOICE
            ══════════════════════════════════════════════════ */}
            {format === "gst" && (
              <div className="rv-gst-invoice">
                {/* Header */}
                <div className="rv-gst-header">
                  <div className="rv-gst-brand">
                    <img src={revaLogo} alt="Reva Jewellers Logo" className="rv-gst-logo-img" />
                    <div className="rv-gst-brand-info">
                      <h1 className="rv-gst-brand-name">{storeInfo.name}</h1>
                      <span className="rv-gst-brand-slogan">{storeInfo.slogan}</span>
                      <span className="rv-gst-brand-sanskrit">{storeInfo.tagline}</span>
                      <div className="rv-gst-store-meta">
                        {storeInfo.address && <div>{storeInfo.address}</div>}
                        {(storeInfo.phone || storeInfo.email) && (
                          <div>
                            {storeInfo.phone && `Phone: ${storeInfo.phone}`}
                            {storeInfo.phone && storeInfo.email && " · "}
                            {storeInfo.email && `Email: ${storeInfo.email}`}
                          </div>
                        )}
                        {(storeInfo.gstin || storeInfo.state) && (
                          <div>
                            {storeInfo.gstin && <span><strong>GSTIN:</strong> {storeInfo.gstin} </span>}
                            {storeInfo.state && <span><strong>State:</strong> {storeInfo.state}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rv-gst-invoice-meta">
                    <span className="rv-gst-badge-title">Tax Invoice</span>
                    <span className="rv-gst-copy-label">Original for Recipient</span>
                    <div style={{ marginTop: 6, fontSize: 10, textAlign: "right" }}>
                      <div><strong>Invoice No:</strong> {invoice.invoiceNumber}</div>
                      <div><strong>Date:</strong> {fmtDate(invoice.invoiceDate)}</div>
                      {storeInfo.state && <div><strong>Place of Supply:</strong> {storeInfo.state}</div>}
                      <div><strong>Reverse Charge:</strong> No</div>
                    </div>
                  </div>
                </div>

                {/* Customer Details Box */}
                <div className="rv-gst-details-grid">
                  <div className="rv-gst-col">
                    <div className="rv-gst-col-title">Billed To (Customer Details)</div>
                    <div className="rv-gst-field-row">
                      <span className="rv-gst-field-lbl">Customer Name:</span>
                      <span className="rv-gst-field-val">{invoice.customerName || "—"}</span>
                    </div>
                    <div className="rv-gst-field-row">
                      <span className="rv-gst-field-lbl">Phone Number:</span>
                      <span className="rv-gst-field-val">{invoice.customerPhone || "—"}</span>
                    </div>
                    {invoice.customerGSTIN && (
                      <div className="rv-gst-field-row">
                        <span className="rv-gst-field-lbl">Customer GSTIN:</span>
                        <span className="rv-gst-field-val">{invoice.customerGSTIN}</span>
                      </div>
                    )}
                  </div>

                  <div className="rv-gst-col">
                    <div className="rv-gst-col-title">Transaction Particulars</div>
                    <div className="rv-gst-field-row">
                      <span className="rv-gst-field-lbl">Invoice Status:</span>
                      <span className="rv-gst-field-val">{invoice.invoiceStatus || "—"}</span>
                    </div>
                    <div className="rv-gst-field-row">
                      <span className="rv-gst-field-lbl">Total Items:</span>
                      <span className="rv-gst-field-val">{lines.length}</span>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="rv-gst-table">
                  <thead>
                    <tr>
                      <th style={{ width: "26px" }} className="rv-text-center">#</th>
                      <th>Description of Goods</th>
                      <th style={{ width: "45px" }} className="rv-text-center">HSN</th>
                      <th style={{ width: "65px" }}>Purity</th>
                      <th style={{ width: "52px" }} className="rv-text-right">Gross (g)</th>
                      <th style={{ width: "50px" }} className="rv-text-right">Less (g)</th>
                      <th style={{ width: "52px" }} className="rv-text-right">Net (g)</th>
                      <th style={{ width: "35px" }} className="rv-text-center">Qty</th>
                      <th style={{ width: "68px" }} className="rv-text-right">Rate (₹)</th>
                      <th style={{ width: "70px" }} className="rv-text-right">Taxable (₹)</th>
                      <th style={{ width: "42px" }} className="rv-text-center">GST%</th>
                      <th style={{ width: "78px" }} className="rv-text-right">Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedLines.map((row) => (
                      <tr key={row.invoiceItemID || row.sr}>
                        <td className="rv-text-center">{row.sr}</td>
                        <td>
                          <div className="rv-gst-prod-name">{row.productName}</div>
                          <div className="rv-gst-prod-sub">
                            Code: {row.productCode}
                            {row.inventoryItemID ? ` · Item #${row.inventoryItemID}` : ""}
                          </div>
                        </td>
                        <td className="rv-text-center">{row.hsn}</td>
                        <td>{row.purity}</td>
                        <td className="rv-text-right">{fmtWt(row.grossWt)}</td>
                        <td className="rv-text-right">{fmtWt(row.lessWt)}</td>
                        <td className="rv-text-right"><strong>{fmtWt(row.netWt)}</strong></td>
                        <td className="rv-text-center">{row.quantity}</td>
                        <td className="rv-text-right">{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
                        <td className="rv-text-right">{parseFloat(row.lineAmt || 0).toFixed(2)}</td>
                        <td className="rv-text-center">{row.taxRate ? `${row.taxRate}%` : "—"}</td>
                        <td className="rv-text-right"><strong>{parseFloat(row.total || 0).toFixed(2)}</strong></td>
                      </tr>
                    ))}

                    {/* Totals Summary Row */}
                    <tr className="rv-gst-totals-row">
                      <td colSpan="4" style={{ textAlign: "right" }}>TOTALS:</td>
                      <td className="rv-text-right">{fmtWt(totals.totGross)}</td>
                      <td className="rv-text-right">{fmtWt(totals.totLess)}</td>
                      <td className="rv-text-right"><strong>{fmtWt(totals.totNet)} g</strong></td>
                      <td className="rv-text-center">
                        {enrichedLines.reduce((s, c) => s + (Number(c.quantity) || 1), 0)}
                      </td>
                      <td className="rv-text-right">—</td>
                      <td className="rv-text-right">{fmtAmt(totals.totTaxable)}</td>
                      <td className="rv-text-center">—</td>
                      <td className="rv-text-right"><strong>{fmtAmt(totals.totGrossAmt)}</strong></td>
                    </tr>
                  </tbody>
                </table>

                {/* Bottom Section: Words & Tax Calculation */}
                <div className="rv-gst-bottom-split">
                  <div>
                    {/* Amount in words */}
                    <div className="rv-gst-words-card">
                      <div className="rv-gst-words-label">Invoice Amount in Words</div>
                      <div className="rv-gst-words-text">
                        {numberToWordsIndian(totals.totGrossAmt)}
                      </div>
                    </div>

                    {/* HSN Summary Table */}
                    {totals.hsnSummary.length > 0 && (
                      <table className="rv-gst-hsn-table">
                        <thead>
                          <tr>
                            <th>HSN / SAC</th>
                            <th>Taxable Val</th>
                            <th>CGST</th>
                            <th>SGST</th>
                            <th>Total Tax</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totals.hsnSummary.map((h) => {
                            const halfTax = h.taxAmt / 2;
                            return (
                              <tr key={h.hsn}>
                                <td>{h.hsn}</td>
                                <td>{fmtAmt(h.taxable)}</td>
                                <td>{fmtAmt(halfTax)}</td>
                                <td>{fmtAmt(halfTax)}</td>
                                <td><strong>{fmtAmt(h.taxAmt)}</strong></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Calculations Box */}
                  <div className="rv-gst-calc-card">
                    <div className="rv-gst-calc-row">
                      <span>Total Taxable Amount</span>
                      <span className="rv-gst-calc-val">{fmtAmt(totals.totTaxable)}</span>
                    </div>
                    <div className="rv-gst-calc-row">
                      <span>CGST</span>
                      <span className="rv-gst-calc-val">{fmtAmt(totals.totTax / 2)}</span>
                    </div>
                    <div className="rv-gst-calc-row">
                      <span>SGST</span>
                      <span className="rv-gst-calc-val">{fmtAmt(totals.totTax / 2)}</span>
                    </div>
                    <div className="rv-gst-calc-row">
                      <span>Total GST</span>
                      <span className="rv-gst-calc-val">{fmtAmt(totals.totTax)}</span>
                    </div>
                    <div className="rv-gst-calc-row rv-gst-calc-row--highlight">
                      <span>GRAND TOTAL (₹)</span>
                      <span className="rv-gst-calc-val">{fmtAmt(totals.totGrossAmt)}</span>
                    </div>
                  </div>
                </div>

                {/* Footer, Terms & Signatures */}
                <div className="rv-gst-footer-section">
                  <div className="rv-gst-terms-sign-grid">
                    <div className="rv-gst-terms">
                      <strong>Terms & Conditions:</strong>
                      <ol>
                        <li>Goods once sold will not be exchanged or returned without original tax invoice.</li>
                        <li>All jewellery items are stamped as per prescribed purity standards.</li>
                        <li>Weight recorded at the time of delivery is final.</li>
                        <li>All disputes are subject to Anand, Gujarat jurisdiction only.</li>
                      </ol>
                    </div>

                    <div className="rv-gst-sign-box">
                      <div className="rv-gst-sign-label">For {storeInfo.name}</div>
                      <div className="rv-gst-sign-line">Authorized Signatory</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════════════════
               FORMAT 2: LUXURY ATELIER RETAIL SLIP
            ══════════════════════════════════════════════════ */}
            {format === "luxury" && (
              <div className="rv-lux-invoice">
                <div className="rv-lux-header">
                  <div className="rv-lux-logo-wrap">
                    <img src={revaLogo} alt="Reva Jewellers" />
                  </div>
                  <h1 className="rv-lux-title">{storeInfo.name}</h1>
                  <span className="rv-lux-slogan">{storeInfo.slogan}</span>
                  <span className="rv-lux-sanskrit">{storeInfo.tagline}</span>
                  {(storeInfo.address || storeInfo.phone || storeInfo.gstin) && (
                    <p style={{ margin: "4px 0 0", fontSize: 9.5, color: "#6e5d4f" }}>
                      {[
                        storeInfo.address,
                        storeInfo.phone && `Phone: ${storeInfo.phone}`,
                        storeInfo.gstin && `GSTIN: ${storeInfo.gstin}`
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </div>

                <div className="rv-lux-meta-bar">
                  <span><strong>INVOICE NO:</strong> {invoice.invoiceNumber}</span>
                  <span><strong>DATE:</strong> {fmtDate(invoice.invoiceDate)}</span>
                  {storeInfo.gstin && <span><strong>GSTIN:</strong> {storeInfo.gstin}</span>}
                </div>

                <div className="rv-lux-customer-bar">
                  <span><strong>CLIENT:</strong> {invoice.customerName || "—"}</span>
                  <span><strong>PHONE:</strong> {invoice.customerPhone || "—"}</span>
                  {storeInfo.state && <span><strong>PLACE:</strong> {storeInfo.state}</span>}
                </div>

                <table className="rv-lux-table">
                  <thead>
                    <tr>
                      <th style={{ width: "30px" }}>#</th>
                      <th>JEWELLERY CREATION</th>
                      <th>PURITY</th>
                      <th style={{ textAlign: "right" }}>NET WT</th>
                      <th style={{ textAlign: "right" }}>RATE (₹)</th>
                      <th style={{ textAlign: "right" }}>AMOUNT (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedLines.map((row) => (
                      <tr key={row.invoiceItemID || row.sr}>
                        <td>{row.sr}</td>
                        <td>
                          <strong>{row.productName}</strong>
                          <div style={{ fontSize: 8.5, color: "#8c6527" }}>
                            Code: {row.productCode} (Gross: {fmtWt(row.grossWt)}g · Less: {fmtWt(row.lessWt)}g)
                          </div>
                        </td>
                        <td>{row.purity}</td>
                        <td style={{ textAlign: "right" }}><strong>{fmtWt(row.netWt)} g</strong></td>
                        <td style={{ textAlign: "right" }}>{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}><strong>{fmtAmt(row.total)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="rv-lux-totals">
                  <div className="rv-lux-totals-box">
                    <div className="rv-lux-totals-row">
                      <span>Total Net Weight:</span>
                      <span><strong>{fmtWt(totals.totNet)} g</strong></span>
                    </div>
                    <div className="rv-lux-totals-row">
                      <span>Subtotal (Excl. Tax):</span>
                      <span>{fmtAmt(totals.totTaxable)}</span>
                    </div>
                    <div className="rv-lux-totals-row">
                      <span>GST:</span>
                      <span>{fmtAmt(totals.totTax)}</span>
                    </div>
                    <div className="rv-lux-totals-row rv-lux-totals-row--grand">
                      <span>GRAND TOTAL:</span>
                      <span style={{ color: "#8c6527" }}>{fmtAmt(totals.totGrossAmt)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ margin: "10px 0 0", fontSize: 9.5, fontStyle: "italic", color: "#554a40" }}>
                  <strong>In Words:</strong> {numberToWordsIndian(totals.totGrossAmt)}
                </div>

                <div className="rv-lux-footer">
                  <div className="rv-lux-seal">
                    <ShieldCheckIcon size={14} />
                    <span>CERTIFIED AUTHENTIC PURITY & HALLMARK GUARANTEE</span>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 9.5 }}>
                    <div style={{ fontWeight: 700 }}>REVA JEWELLERS</div>
                    <div style={{ marginTop: 35, borderTop: "1px solid #1a1614", width: 140, paddingTop: 3 }}>
                      Signature
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}

/* ── SVG Icons (following project design system) ── */
function PrinterIcon({ size = 15, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function SettingsIcon({ size = 15, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function CloseIcon({ size = 15, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function FitScreenIcon({ size = 13, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <polyline points="14 20 14 14 20 14" />
      <polyline points="10 4 10 10 4 10" />
    </svg>
  );
}

function ZoomIcon({ size = 13, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function MoveHorizontalIcon({ size = 14, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <polyline points="18 8 22 12 18 16" />
      <polyline points="6 8 2 12 6 16" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  );
}

function ShieldCheckIcon({ size = 13, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
